import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { message } from '@myua/kit';
import { Dispatch } from 'redux';
import { adopted } from '../../../general/apiScenarios';
import { Asset } from '../../api';
import { getAssetLabLogger } from '../app/logs';
import * as finderActions from '../finder/actions';

export function useRemover() {
  const dispatch = useDispatch();
  const assetLabLogger = useSelector(getAssetLabLogger);
  const [inProgress, setInProgress] = useState(false);

  const remove = useCallback(
    async (assetId: Asset['id']) => {
      setInProgress(true);
      try {
        const result = await adopted.removerAPI.deleteNode(assetId);
        if (result.status >= 400) {
          void message.error('Failed to delete file');
          return false;
        }
        void message.success('File deleted');
        assetLabLogger({ action: 'File', sub_action: 'Delete', extra: { int_1: assetId } });
        // force: список этой локации закэширован, а на сервере файла больше нет —
        // без него удалённый файл остался бы в сетке.
        (dispatch as Dispatch)(finderActions.loadAssetsRequested({ force: true }));
        (dispatch as Dispatch)(
          finderActions.assetSelected({ ids: [], firstId: undefined, lastId: undefined }),
        );
        return true;
      } catch {
        void message.error('Failed to delete file');
        return false;
      } finally {
        setInProgress(false);
      }
    },
    [dispatch, assetLabLogger],
  );

  const removeMany = useCallback(
    async (assetIds: Asset['id'][]) => {
      if (assetIds.length === 0) return false;
      setInProgress(true);
      try {
        const results = await Promise.all(
          assetIds.map((id) =>
            adopted.removerAPI
              .deleteNode(id)
              .then((r) => ({ id, ok: r.status < 400 }))
              .catch(() => ({ id, ok: false })),
          ),
        );
        const failed = results.filter((r) => !r.ok);
        const successCount = results.length - failed.length;

        results
          .filter((r) => r.ok)
          .forEach((r) =>
            assetLabLogger({ action: 'File', sub_action: 'Delete', extra: { int_1: r.id } }),
          );

        if (successCount > 0) {
          void message.success(
            successCount === 1 ? 'File deleted' : `${successCount} files deleted`,
          );
        }
        if (failed.length > 0) {
          void message.error(
            failed.length === 1
              ? 'Failed to delete 1 file'
              : `Failed to delete ${failed.length} files`,
          );
        }

        // force: список этой локации закэширован, а на сервере файла больше нет —
        // без него удалённый файл остался бы в сетке.
        (dispatch as Dispatch)(finderActions.loadAssetsRequested({ force: true }));
        (dispatch as Dispatch)(
          finderActions.assetSelected({ ids: [], firstId: undefined, lastId: undefined }),
        );
        return failed.length === 0;
      } finally {
        setInProgress(false);
      }
    },
    [dispatch, assetLabLogger],
  );

  return { remove, removeMany, inProgress };
}
